import { useState } from 'react';
import './EditPlan.css';

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_PLAN = {
  id: 'demo-plan-001',
  nanoid: 'demo',
  name: '4-Week Kickstart',
  description: 'A balanced intro plan mixing running, strength, and mobility.',
  focus: 'general fitness',
  duration_weeks: 4,
};

const MOCK_TEMPLATES = [
  {
    id: 'tmpl-001',
    nanoid: 'tmpl001nanoid',
    name: 'Morning Run',
    modality: 'running',
    difficulty: 'intermediate',
    duration_minutes: 30,
    delivery_time: '07:00',
    day_slots: [
      { week_number: 1, day_number: 1, day_name: 'Mon' },
      { week_number: 1, day_number: 4, day_name: 'Thu' },
      { week_number: 2, day_number: 1, day_name: 'Mon' },
      { week_number: 2, day_number: 4, day_name: 'Thu' },
    ],
  },
  {
    id: 'tmpl-002',
    nanoid: 'tmpl002nanoid',
    name: 'Strength Training',
    modality: 'strength',
    difficulty: 'beginner',
    duration_minutes: 45,
    delivery_time: '08:00',
    day_slots: [
      { week_number: 1, day_number: 2, day_name: 'Tue' },
      { week_number: 1, day_number: 5, day_name: 'Fri' },
      { week_number: 2, day_number: 2, day_name: 'Tue' },
    ],
  },
  {
    id: 'tmpl-003',
    nanoid: 'tmpl003nanoid',
    name: 'Pilates Class',
    modality: 'group_class',
    difficulty: 'beginner',
    duration_minutes: 60,
    delivery_time: '18:00',
    day_slots: [
      { week_number: 1, day_number: 3, day_name: 'Wed' },
      { week_number: 2, day_number: 3, day_name: 'Wed' },
    ],
  },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const MODALITY_OPTIONS = [
  { label: 'Yoga',        value: 'yoga' },
  { label: 'Strength',    value: 'strength' },
  { label: 'Cardio',      value: 'cardio' },
  { label: 'HIIT',        value: 'hiit' },
  { label: 'Running',     value: 'running' },
  { label: 'Mobility',    value: 'mobility' },
  { label: 'Core',        value: 'core' },
  { label: 'Stretch',     value: 'stretch' },
  { label: 'Group Class', value: 'group_class' },
  { label: 'Other',       value: 'other' },
];

const DIFFICULTY_OPTIONS = [
  { label: 'Beginner',     value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced',     value: 'advanced' },
];

const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90];

const SCOPE_OPTIONS = [
  { label: 'Remaining Plan', value: 'remaining',  desc: 'All future workouts' },
  { label: 'This Week',      value: 'this_week',  desc: 'Current week only'   },
  { label: 'Next Week',      value: 'next_week',  desc: 'Upcoming week only'  },
];

const INTENSITY_OPTIONS = [
  { label: '↓ Lighter',    value: 'lighter', desc: 'Fewer sets & reps' },
  { label: 'Keep current', value: null,       desc: 'No volume change'  },
  { label: '↑ Harder',     value: 'harder',  desc: 'More sets & reps'  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface TemplateEdit {
  new_name:            string;
  new_modality:        string;
  new_notes:           string;
  new_difficulty:      string;
  new_time:            string;
  new_duration_minutes: number | null;
}

function uniqueDayNames(slots: typeof MOCK_TEMPLATES[0]['day_slots']): string {
  const names = [...new Set(slots.map(s => s.day_name))];
  return names.join('/') || 'No remaining sessions';
}

// ─── Demo page ────────────────────────────────────────────────────────────────

export default function EditPlanDemo() {
  const [planName, setPlanName] = useState(MOCK_PLAN.name);
  const [scope, setScope] = useState('remaining');
  const [intensity, setIntensity] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [edits, setEdits] = useState<Record<string, TemplateEdit>>(() => {
    const init: Record<string, TemplateEdit> = {};
    for (const t of MOCK_TEMPLATES) {
      init[t.id] = {
        new_name:             t.name,
        new_modality:         t.modality,
        new_notes:            '',
        new_difficulty:       t.difficulty,
        new_time:             t.delivery_time,
        new_duration_minutes: null,
      };
    }
    return init;
  });

  const updateEdit = <K extends keyof TemplateEdit>(id: string, field: K, value: TemplateEdit[K]) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const hasChanges = () => {
    if (intensity !== null) return true;
    if (freeText.trim()) return true;
    if (planName !== MOCK_PLAN.name) return true;
    for (const t of MOCK_TEMPLATES) {
      const e = edits[t.id];
      if (!e) continue;
      if (e.new_name !== t.name) return true;
      if (e.new_modality !== t.modality) return true;
      if (e.new_difficulty !== t.difficulty) return true;
      if (e.new_time !== t.delivery_time) return true;
      if (e.new_duration_minutes !== null) return true;
    }
    return false;
  };

  if (submitted) {
    return (
      <div className="ep-page">
        <div className="ep-state-screen">
          <div className="ep-success-icon">✓</div>
          <h2 className="ep-success-title">Plan updated! (demo)</h2>
          <p className="ep-state-msg">In production this would apply changes and regenerate exercises.</p>
          <pre style={{ textAlign: 'left', background: '#f7f7f5', borderRadius: 8, padding: 16, fontSize: 11, overflowX: 'auto', marginTop: 16, maxWidth: 420 }}>
            {JSON.stringify({ planName, scope, intensity, edits, freeText }, null, 2)}
          </pre>
          <button className="ep-btn ep-btn-primary" style={{ marginTop: 24 }} onClick={() => setSubmitted(false)}>
            Back to editor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ep-page">
      <div className="ep-root">
        <div className="ep-container">

          {/* Back + header */}
          <div className="ep-back-row">
            <button className="ep-back-btn" onClick={() => window.history.back()}>
              ← Back to plan
            </button>
          </div>
          <div className="ep-header">
            <h1 className="ep-title">Edit Plan</h1>
            <p style={{ fontSize: 12, color: '#aaa', margin: 0 }}>Demo — no data is saved</p>
          </div>

          {/* Plan name */}
          <div className="ep-section">
            <h2 className="ep-section-title">Plan name</h2>
            <input
              type="text"
              className="ep-name-input"
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              placeholder="My Workout Plan"
              maxLength={120}
            />
          </div>

          {/* Scope */}
          <div className="ep-section">
            <h2 className="ep-section-title">Apply changes to</h2>
            <div className="ep-scope-row">
              {SCOPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`ep-scope-btn${scope === opt.value ? ' ep-scope-btn--active' : ''}`}
                  onClick={() => setScope(opt.value)}
                >
                  <span className="ep-scope-label">{opt.label}</span>
                  <span className="ep-scope-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Intensity */}
          <div className="ep-section">
            <h2 className="ep-section-title">Intensity</h2>
            <p className="ep-section-hint">Scales sets, reps, and duration up or down across all workouts.</p>
            <div className="ep-intensity-row">
              {INTENSITY_OPTIONS.map(opt => (
                <button
                  key={String(opt.value)}
                  className={`ep-intensity-btn${intensity === opt.value ? ' ep-intensity-btn--active' : ''}`}
                  onClick={() => setIntensity(opt.value)}
                >
                  <span className="ep-intensity-label">{opt.label}</span>
                  <span className="ep-intensity-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Workout templates */}
          <div className="ep-section">
            <h2 className="ep-section-title">Your Workouts</h2>
            <p className="ep-section-hint">Each card is a workout template. Changes apply to every future instance.</p>
            <div className="ep-template-list">
              {MOCK_TEMPLATES.map(t => {
                const e = edits[t.id];
                if (!e) return null;
                const currentDuration = e.new_duration_minutes ?? t.duration_minutes;
                const isExternal = e.new_modality === 'group_class' || e.new_modality === 'other';
                return (
                  <div key={t.id} className="ep-template-card">
                    <div className="ep-template-header">
                      <span className="ep-template-days">{uniqueDayNames(t.day_slots)}</span>
                    </div>

                    {/* Name */}
                    <div className="ep-config-row">
                      <span className="ep-config-label">Name</span>
                      <input
                        type="text"
                        className="ep-name-input ep-name-input--workout"
                        value={e.new_name}
                        onChange={ev => updateEdit(t.id, 'new_name', ev.target.value)}
                        placeholder="Workout name"
                        maxLength={120}
                      />
                    </div>

                    {/* Type pills */}
                    <div className="ep-config-row">
                      <span className="ep-config-label">Type</span>
                      <div className="ep-pills">
                        {MODALITY_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            className={`ep-pill${e.new_modality === opt.value ? ' ep-pill--active' : ''}`}
                            onClick={() => updateEdit(t.id, 'new_modality', opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Class/activity name */}
                    {isExternal && (
                      <div className="ep-config-row">
                        <span className="ep-config-label">
                          {e.new_modality === 'group_class' ? 'Class name' : 'Activity name'}
                        </span>
                        <input
                          type="text"
                          className="ep-name-input ep-name-input--workout"
                          value={e.new_notes}
                          onChange={ev => updateEdit(t.id, 'new_notes', ev.target.value)}
                          placeholder={e.new_modality === 'group_class' ? 'e.g. Pilates, Spin, Barre...' : 'e.g. Rock climbing, Tennis...'}
                          maxLength={80}
                        />
                      </div>
                    )}

                    {/* Level */}
                    <div className="ep-config-row">
                      <span className="ep-config-label">Level</span>
                      <div className="ep-pills">
                        {DIFFICULTY_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            className={`ep-pill${e.new_difficulty === opt.value ? ' ep-pill--active' : ''}`}
                            onClick={() => updateEdit(t.id, 'new_difficulty', opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="ep-config-row">
                      <span className="ep-config-label">Duration</span>
                      <div className="ep-pills ep-pills--duration">
                        {DURATION_OPTIONS.map(min => (
                          <button
                            key={min}
                            className={`ep-pill${currentDuration === min ? ' ep-pill--active' : ''}`}
                            onClick={() => updateEdit(t.id, 'new_duration_minutes', min)}
                          >
                            {min} min
                          </button>
                        ))}
                      </div>
                      {currentDuration && !DURATION_OPTIONS.includes(currentDuration) && (
                        <p className="ep-duration-current">Current: {currentDuration} min</p>
                      )}
                    </div>

                    {/* Delivery time */}
                    <div className="ep-config-row">
                      <span className="ep-config-label">Delivery time</span>
                      <input
                        type="time"
                        value={e.new_time}
                        onChange={ev => updateEdit(t.id, 'new_time', ev.target.value)}
                        className="ep-time-input"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Free text */}
          <div className="ep-section">
            <h2 className="ep-section-title">Anything else?</h2>
            <p className="ep-section-hint">Injuries, equipment you don't have, specific requests.</p>
            <textarea
              className="ep-textarea"
              rows={4}
              value={freeText}
              onChange={e => setFreeText(e.target.value)}
              placeholder="e.g. I hurt my knee — avoid jumping. I don't have a pull-up bar."
              maxLength={500}
            />
            {freeText.length > 0 && (
              <p className="ep-char-count">{freeText.length}/500</p>
            )}
          </div>

          <button
            className="ep-btn ep-btn-primary ep-btn-submit"
            onClick={() => setSubmitted(true)}
            disabled={!hasChanges()}
          >
            Update Plan
          </button>

          <p className="ep-submit-hint">
            Exercise changes use AI to regenerate workouts and may take 15–30 seconds.
          </p>

        </div>
      </div>

      <footer className="ep-footer">
        <div className="ep-footer-inner">
          <span className="ep-footer-brand">Mosaime</span>
          <p className="ep-footer-copy">&copy; {new Date().getFullYear()} Mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
